<template>
  <section class="videos-content mt-10 mb-10 px-4 lg:px-0" id="videos">
    <div class="max-w-screen-xl mx-auto">
      <div class="mb-8">
        <div class="flex items-center gap-2 relative pb-3">
          <div>
            <h2 class="text-lg md:text-xl font-medium tracking-wide"
              :style="{ color: customization?.section_title_color || '#1c243b' }">
              {{ customization?.videos_title || 'Vídeos' }}
            </h2>
            <h2 v-if="customization?.videos_subtitle" class="text-xs md:text-sm font-normal tracking-wide"
              :style="{ color: customization?.section_subtitle_color || '#666666' }">
              {{ customization.videos_subtitle }}
            </h2>
          </div>
          <div class="absolute bottom-0 left-0 w-full h-px bg-gray-200"></div>
          <div class="absolute bottom-0 left-0 h-px w-full max-w-[480px]"
            :style="{ backgroundColor: customization?.section_divider_color || '#e5e7eb' }"></div>
        </div>
      </div>

      <div v-if="videos && videos.length > 0">
        <div class="video-frame relative w-full bg-black rounded-lg overflow-hidden shadow-md" style="padding-top: 56.25%;">
          <iframe
            v-if="activeEmbedUrl"
            :src="activeEmbedUrl"
            class="absolute inset-0 w-full h-full"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>

        <h3 v-if="activeVideo?.title" class="mt-4 text-base font-medium" :style="{ color: customization?.section_title_color || '#1c243b' }">
          {{ activeVideo.title }}
        </h3>

        <div v-if="videos.length > 1" class="mt-6 thumbs-scroll flex gap-3 overflow-x-auto pb-2">
          <button
            v-for="video in videos"
            :key="video.id"
            type="button"
            class="thumb-card flex-shrink-0 text-left rounded-lg overflow-hidden border transition-all"
            :class="activeVideo && activeVideo.id === video.id ? 'thumb-active' : 'border-transparent hover:border-gray-300'"
            @click="setActive(video)"
          >
            <div class="thumb-image relative bg-black" style="width: 200px; padding-top: 56.25%;">
              <img
                v-if="video.thumbnail_url"
                :src="video.thumbnail_url"
                :alt="video.title || 'Video'"
                class="absolute inset-0 w-full h-full object-cover"
              />
              <div class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                <div class="bg-white bg-opacity-90 rounded-full w-10 h-10 flex items-center justify-center">
                  <svg class="w-5 h-5 text-gray-800 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.3 2.84A1 1 0 004.84 3.7v12.6a1 1 0 001.46.86l11.54-6.3a1 1 0 000-1.72L6.3 2.84z"/>
                  </svg>
                </div>
              </div>
            </div>
            <div v-if="video.title" class="px-2 py-2">
              <p class="text-xs font-medium text-gray-800 line-clamp-2" style="width: 200px;">{{ video.title }}</p>
            </div>
          </button>
        </div>
      </div>

      <div v-else class="text-center py-12">
        <p class="text-gray-500">Nenhum vídeo disponível ainda.</p>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useLandingPage } from '@/composables/useLandingPage'

export default {
  name: 'VideosSection',
  setup() {
    const { videos, customization } = useLandingPage()

    const activeVideo = ref(null)

    watch(videos, (list) => {
      if (list && list.length > 0 && !activeVideo.value) {
        activeVideo.value = list[0]
      }
      if (activeVideo.value && list && !list.find(v => v.id === activeVideo.value.id)) {
        activeVideo.value = list[0] || null
      }
    }, { immediate: true })

    const setActive = (video) => {
      activeVideo.value = video
    }

    const buildEmbedUrl = (video) => {
      if (!video) return null
      if (video.provider === 'youtube') {
        return `https://www.youtube.com/embed/${video.external_id}?rel=0`
      }
      if (video.provider === 'vimeo') {
        return `https://player.vimeo.com/video/${video.external_id}`
      }
      return null
    }

    const activeEmbedUrl = computed(() => buildEmbedUrl(activeVideo.value))

    return {
      videos,
      customization,
      activeVideo,
      activeEmbedUrl,
      setActive
    }
  }
}
</script>

<style scoped>
.thumbs-scroll {
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}
.thumbs-scroll::-webkit-scrollbar {
  height: 6px;
}
.thumbs-scroll::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}
.thumb-card {
  background: #fff;
  cursor: pointer;
}
.thumb-active {
  border-color: var(--button-color, #4A6CF7) !important;
  box-shadow: 0 0 0 1px var(--button-color, #4A6CF7);
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
